import { useCallback, useEffect, useState } from 'react';
import {
  brandopsApi,
  type BrandHealthRecord,
  type BrandOpsDashboard,
  type BrandRecommendation,
  type BrandRecord,
  type BrandRuleRecord,
  type ComplianceEventRecord,
  type InvestigationRecord,
  type ProtectedAssetRecord,
  type TrademarkRecord,
} from '../../api/brandops';

type Tab = 'dashboard' | 'registry' | 'trademarks' | 'rules' | 'assets' | 'compliance' | 'investigations' | 'health' | 'recommendations';

function statusClass(s: string) {
  if (s === 'active' || s === 'registered' || s === 'pass' || s === 'resolved') return 'text-emerald-700 bg-emerald-50';
  if (s === 'pending' || s === 'open' || s === 'warning' || s === 'renewal_due') return 'text-amber-700 bg-amber-50';
  if (s === 'fail' || s === 'escalated' || s === 'suspended' || s === 'expired') return 'text-red-700 bg-red-50';
  if (s === 'in_progress' || s === 'under_review') return 'text-sky-700 bg-sky-50';
  return 'text-slate-600 bg-slate-50';
}

function scoreClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-orange-700 bg-orange-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

export function BrandOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<BrandOpsDashboard | null>(null);
  const [brands, setBrands] = useState<BrandRecord[]>([]);
  const [trademarks, setTrademarks] = useState<TrademarkRecord[]>([]);
  const [rules, setRules] = useState<BrandRuleRecord[]>([]);
  const [assets, setAssets] = useState<ProtectedAssetRecord[]>([]);
  const [compliance, setCompliance] = useState<ComplianceEventRecord[]>([]);
  const [investigations, setInvestigations] = useState<InvestigationRecord[]>([]);
  const [health, setHealth] = useState<BrandHealthRecord[]>([]);
  const [recommendations, setRecommendations] = useState<BrandRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, br, tm, rl, ast, comp, inv, hlth, recs] = await Promise.all([
        brandopsApi.dashboard(), brandopsApi.listBrands(), brandopsApi.listTrademarks(),
        brandopsApi.listRules(), brandopsApi.listAssets(), brandopsApi.listCompliance(),
        brandopsApi.listInvestigations(), brandopsApi.listHealth(), brandopsApi.listRecommendations(),
      ]);
      setDashboard(dash); setBrands(br); setTrademarks(tm); setRules(rl);
      setAssets(ast); setCompliance(comp); setInvestigations(inv); setHealth(hlth); setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load brand operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await brandopsApi.generateRecommendations(); await refresh(); } finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Brand Dashboard' },
    { id: 'registry', label: 'Brand Registry' },
    { id: 'trademarks', label: 'Trademark Manager' },
    { id: 'rules', label: 'Brand Rules' },
    { id: 'assets', label: 'Protected Assets' },
    { id: 'compliance', label: 'Compliance Center' },
    { id: 'investigations', label: 'Investigations' },
    { id: 'health', label: 'Brand Health' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Brand Registry &amp; Governance</h1>
          <p className="text-sm text-slate-500">Brand protection, IP governance, compliance &amp; AI brand copilot — APP-00005</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Registered Brands', value: dashboard.registered_brands.toString() },
            { label: 'Active Trademarks', value: dashboard.active_trademarks.toString() },
            { label: 'Renewals Due', value: dashboard.renewals_due.toString() },
            { label: 'Compliance Violations', value: dashboard.compliance_violations.toString() },
            { label: 'Open Investigations', value: dashboard.open_investigations.toString() },
            { label: 'Avg Health Score', value: dashboard.avg_health_score.toFixed(0) },
            { label: 'Avg Compliance', value: dashboard.avg_compliance_score.toFixed(0) },
            { label: 'Protected Assets', value: dashboard.protected_assets.toString() },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'registry' && (
        <div className="space-y-3">
          {brands.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium text-slate-900">{b.brand_name}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(b.registry_status)}`}>{b.registry_status}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span className={scoreClass(b.health_score)}>Health: {b.health_score.toFixed(0)}</span>
                <span>Compliance: {b.compliance_score.toFixed(0)}</span>
                <span>Consistency: {b.consistency_score.toFixed(0)}</span>
                <span>Protection: {b.protection_score.toFixed(0)}</span>
                <span>{b.trademark_count} trademarks</span>
              </div>
              {b.marketplaces && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {b.marketplaces.map((m) => (
                    <span key={m} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'trademarks' && (
        <div className="space-y-3">
          {trademarks.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{t.trademark_number}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>{t.status.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-500">{t.country} · {t.trademark_class}</span>
              </div>
              {t.days_until_renewal != null && t.days_until_renewal <= 90 && (
                <p className="mt-1 text-sm text-amber-600">Renewal in {t.days_until_renewal} days</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'rules' && (
        <div className="space-y-3">
          {rules.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{r.name}</span>
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">{r.scope}</span>
                {r.requires_approval && <span className="text-xs text-amber-600">Requires approval</span>}
              </div>
              {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
              {r.rule_text && <p className="mt-1 text-xs text-slate-500 font-mono">{r.rule_text}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'assets' && (
        <div className="space-y-3">
          {assets.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{a.name}</span>
                <span className="text-xs text-slate-400">{a.asset_type}</span>
                {a.is_protected && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Protected</span>}
              </div>
              <p className="mt-1 text-xs text-slate-500">{a.usage_count} usages</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'compliance' && (
        <div className="space-y-3">
          {compliance.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                {c.sku && <span className="font-mono text-sm">{c.sku}</span>}
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>{c.status}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(c.severity)}`}>{c.severity}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{c.description}</p>
              {c.policy && <p className="mt-1 text-xs text-slate-400">{c.policy.replace(/_/g, ' ')}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'investigations' && (
        <div className="space-y-3">
          {investigations.map((i) => (
            <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{i.case_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(i.status)}`}>{i.status.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-400">{i.type?.replace(/_/g, ' ')}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{i.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="space-y-3">
          {health.map((h) => (
            <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{h.brand_name}</span>
                <span className={`font-medium ${scoreClass(h.health_score)}`}>{h.health_score.toFixed(0)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(h.severity)}`}>{h.severity}</span>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>Compliance: {h.compliance_score.toFixed(0)}</span>
                <span>Consistency: {h.consistency_score.toFixed(0)}</span>
                <span>Protection: {h.protection_score.toFixed(0)}</span>
                {h.ai_alignment_score != null && <span>AI Alignment: {(h.ai_alignment_score * 100).toFixed(0)}%</span>}
              </div>
              {h.issue && <p className="mt-1 text-sm text-slate-600">{h.issue}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
